// Shared source of truth for the native invoice "Design" step: background
// color presets, the printer-friendly default, the selectable fonts, and the
// helpers that derive the monogram accent color. Consumed by the frontend
// Design section + live preview and by the backend PDF/email EJS renderer so
// the same swatch always maps to the same accent everywhere.

import {
    HEX_COLOR_REGEX,
    hexToRgba,
    isValidHexColor,
    normalizeHexColor,
} from "../helpers/color";

export { HEX_COLOR_REGEX, isValidHexColor, normalizeHexColor };

export interface InvoiceBackgroundPreset {
    id: string;
    label: string;
    // Invoice paper background.
    background: string;
    // Monogram (initials) text color, a darker shade of the same hue.
    accent: string;
}

// The printer-friendly default renders a white page with the neutral monogram.
export const PRINTER_FRIENDLY_BACKGROUND_ID = "printerFriendly";

export const INVOICE_DEFAULT_MONOGRAM_ACCENT = "#515d71";

// Default document text color when no override is set — matches the
// hardcoded body/heading color the renderer used before this field existed.
export const INVOICE_DEFAULT_TEXT_COLOR = "#0f1826";

export const INVOICE_BACKGROUND_PRESETS: InvoiceBackgroundPreset[] = [
    { id: "mint", label: "Mint", background: "#f2fcf7", accent: "#147244" },
    { id: "sky", label: "Sky", background: "#f2faff", accent: "#115f9e" },
    { id: "lilac", label: "Lilac", background: "#f9f8ff", accent: "#5b40ab" },
    { id: "blush", label: "Blush", background: "#fff5f8", accent: "#a7284c" },
    {
        id: "butter",
        label: "Butter",
        background: "#fdf6ef",
        accent: "#a86534",
    },
];

export interface InvoiceTextColorPreset {
    id: string;
    label: string;
    color: string;
}

// Curated dark shades so the default swatch order roughly mirrors the
// Background presets' hues (mint/sky/lilac/blush) at a legible darkness for
// body text on either a light or dark paper background. The first entry is
// the flat default (INVOICE_DEFAULT_TEXT_COLOR) so it always appears
// selected on a fresh invoice, matching the Background section's
// printer-friendly tile.
export const INVOICE_TEXT_COLOR_PRESETS: InvoiceTextColorPreset[] = [
    { id: "default", label: "Default", color: INVOICE_DEFAULT_TEXT_COLOR },
    { id: "forest", label: "Forest", color: "#0f3d24" },
    { id: "navy", label: "Navy", color: "#0d2d4a" },
    { id: "plum", label: "Plum", color: "#2c1a4a" },
    { id: "maroon", label: "Maroon", color: "#4a1420" },
];

export interface InvoiceFontOption {
    id: string;
    label: string;
    // CSS font-family stack applied to the invoice document.
    stack: string;
}

export const DEFAULT_INVOICE_FONT_ID = "inter";

// Each option is backed by an embeddable Google Font so the chosen font renders
// identically in the live React preview AND the headless-Chromium PDF/email
// (which has no Windows/Mac system fonts). The classic options use metric-
// compatible open substitutes (Arimo≈Arial/Helvetica, Tinos≈Times New Roman,
// Gelasio≈Georgia, Cousine≈Courier New) so the familiar label stays accurate to
// the eye. Keep INVOICE_FONTS_STYLESHEET_URL in sync with these families.
export const INVOICE_FONTS: InvoiceFontOption[] = [
    {
        id: "arial",
        label: "Arial",
        stack: '"Arimo", Arial, Helvetica, sans-serif',
    },
    {
        id: "courier",
        label: "Courier",
        stack: '"Cousine", "Courier New", Courier, monospace',
    },
    {
        id: "georgia",
        label: "Georgia",
        stack: '"Gelasio", Georgia, serif',
    },
    {
        id: "helvetica",
        label: "Helvetica",
        stack: '"Arimo", "Helvetica Neue", Helvetica, Arial, sans-serif',
    },
    { id: "inter", label: "Inter", stack: '"Inter", sans-serif' },
    {
        id: "times",
        label: "Times New Roman",
        stack: '"Tinos", "Times New Roman", Times, serif',
    },
];

// Google Fonts stylesheet covering every non-Inter family referenced above
// (Inter is already self-loaded by the app). Embedded in the app (index.html)
// so the Design-step font options render in the live preview.
export const INVOICE_FONTS_STYLESHEET_URL =
    "https://fonts.googleapis.com/css2?family=Arimo:wght@400;500;700&family=Cousine:wght@400;700&family=Gelasio:wght@400;500;700&family=Tinos:wght@400;700&display=swap";

// The PDF renderer runs in headless Chromium with no app bundle and no system
// Inter, so its stylesheet must also carry Inter — otherwise the default
// invoice font silently falls back to a generic sans and the PDF no longer
// matches the on-screen preview.
export const INVOICE_PDF_FONTS_STYLESHEET_URL =
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&family=Arimo:wght@400;500;700&family=Cousine:wght@400;700&family=Gelasio:wght@400;500;700&family=Tinos:wght@400;700&display=swap";

function findPresetByBackground(
    backgroundColor: string | null,
): InvoiceBackgroundPreset | undefined {
    if (!backgroundColor) {
        return undefined;
    }
    const normalized = normalizeHexColor(backgroundColor);
    return INVOICE_BACKGROUND_PRESETS.find(
        (preset) => preset.background === normalized,
    );
}

function clampChannel(value: number): number {
    return Math.max(0, Math.min(255, Math.round(value)));
}

// Darken an arbitrary custom color toward a legible monogram text color. We
// scale each RGB channel down so the accent keeps the chosen hue while being
// dark enough to read on the (lighter) paper background.
export function deriveAccentColor(backgroundColor: string): string {
    const normalized = normalizeHexColor(backgroundColor);
    const match = /^#([0-9a-f]{6})$/.exec(normalized);
    if (!match) {
        return INVOICE_DEFAULT_MONOGRAM_ACCENT;
    }
    const hex = match[1];
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const darkenFactor = 0.45;
    return `#${[r, g, b]
        .map((channel) =>
            clampChannel(channel * darkenFactor)
                .toString(16)
                .padStart(2, "0"),
        )
        .join("")}`;
}

export function getInvoiceAccentColor(
    backgroundColor: string | null,
    accentColor?: string | null,
): string {
    if (accentColor && isValidHexColor(accentColor)) {
        return normalizeHexColor(accentColor);
    }
    if (!backgroundColor) {
        return INVOICE_DEFAULT_MONOGRAM_ACCENT;
    }
    const preset = findPresetByBackground(backgroundColor);
    if (preset) {
        return preset.accent;
    }
    return deriveAccentColor(backgroundColor);
}

export function getInvoiceFontStack(fontId: string | null): string {
    const match = INVOICE_FONTS.find((font) => font.id === fontId);
    const defaultFont = INVOICE_FONTS.find(
        (font) => font.id === DEFAULT_INVOICE_FONT_ID,
    );
    return match ? match.stack : (defaultFont ?? INVOICE_FONTS[0]).stack;
}

// Resolves the primary document text color: a valid custom override, or the
// near-black default. Unlike the accent color (which falls back through
// background presets), text color has a single flat default — it is
// independent of the chosen background.
export function getInvoiceTextColor(
    textColor: string | null | undefined,
): string {
    if (textColor && isValidHexColor(textColor)) {
        return normalizeHexColor(textColor);
    }
    return INVOICE_DEFAULT_TEXT_COLOR;
}

// Secondary/muted copy (field labels, party meta, line descriptions) and
// hairline borders are tints of the primary text color rather than
// independently pickable — this keeps the document's visual hierarchy intact
// (secondary text always reads lighter than primary) no matter which text
// color is chosen, including for dark backgrounds paired with a light
// override.
export function getInvoiceMutedTextColor(textColor: string): string {
    return hexToRgba(textColor, 0.72);
}

export function getInvoiceSubtleTextColor(textColor: string): string {
    return hexToRgba(textColor, 0.55);
}

export function getInvoiceBorderColor(textColor: string): string {
    return hexToRgba(textColor, 0.13);
}
